import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useIdeasStore, type IdeaKind } from '@/store/ideas-store';
import { colors, radius, spacing } from '@/theme/tokens';

/** Submit an idea or bug — mock: nowe_zgłoszenie. Goes onto the public board. */
export default function NewIdeaScreen() {
  const { t } = useTranslation();
  const addIdea = useIdeasStore((s) => s.addIdea);

  const [kind, setKind] = useState<IdeaKind>('IDEA');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const valid = title.trim().length > 0 && description.trim().length > 0;

  const submit = async () => {
    await addIdea({ kind, title: title.trim(), description: description.trim() });
    router.back();
  };

  const TypeCard = ({ value, icon, labelKey }: { value: IdeaKind; icon: keyof typeof Ionicons.glyphMap; labelKey: string }) => {
    const active = kind === value;
    return (
      <Pressable
        accessibilityRole="button"
        onPress={() => setKind(value)}
        style={{
          flex: 1,
          alignItems: 'center',
          gap: spacing.xs,
          paddingVertical: spacing.lg,
          borderRadius: radius.card,
          borderWidth: 2,
          borderColor: active ? colors.primary : colors.divider,
          backgroundColor: active ? colors.primaryTile : colors.card,
        }}
      >
        <Ionicons name={icon} size={30} color={active ? colors.navy : colors.primary} />
        <AppText variant="headlineSm" color={active ? colors.navy : colors.textPrimary}>
          {t(labelKey)}
        </AppText>
      </Pressable>
    );
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl, flexGrow: 1 }}
        >
          <ScreenHeader title={t('ideas.newTitle')} />

          <View style={{ flexDirection: 'row', gap: spacing.sm }}>
            <TypeCard value="IDEA" icon="bulb-outline" labelKey="ideas.kindIdea" />
            <TypeCard value="BUG" icon="bug-outline" labelKey="ideas.kindBug" />
          </View>

          <TextField
            label={t('ideas.fieldTitle')}
            placeholder={t('ideas.fieldTitlePlaceholder')}
            value={title}
            onChangeText={setTitle}
          />
          <TextField
            label={t('ideas.fieldDescription')}
            placeholder={t('ideas.fieldDescriptionPlaceholder')}
            value={description}
            onChangeText={setDescription}
            multiline
            style={{ minHeight: 140, textAlignVertical: 'top', paddingTop: spacing.sm }}
          />

          <View style={{ flex: 1 }} />

          <AppText variant="labelMd" color={colors.outline} style={{ textAlign: 'center' }}>
            {t('ideas.newNote')}
          </AppText>
          <PrimaryButton label={t('ideas.newSubmit')} disabled={!valid} onPress={submit} />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
